/**
 * SessionStorage persistence for SubmissionsList filters/search/sort/pagination.
 *
 * URL params remain the source of truth for shareable links and browser
 * back/forward navigation. SessionStorage acts as a safety net so the filter
 * state survives in-app navigation that clears the URL (e.g. clicking the
 * "Submissions" link in the side nav).
 */

export const SUBMISSIONS_LIST_STORAGE_KEY = "staff-ui:submissions-list:filters";

export type SubmissionsListPersistedState = {
  publication: string;
  region: string;
  edition: string;
  practiceArea: string;
  contactMatch: string;
  search: string;
  page: string;
  sortField: string;
  sortDirection: string;
  hideIncomplete: string;
  taxonomy: string;
};

export type SubmissionsListPersistedKey = keyof SubmissionsListPersistedState;

export const SUBMISSIONS_LIST_PERSISTED_KEYS: SubmissionsListPersistedKey[] = [
  "publication",
  "region",
  "edition",
  "practiceArea",
  "contactMatch",
  "search",
  "page",
  "sortField",
  "sortDirection",
  "hideIncomplete",
  "taxonomy",
];

/**
 * Read the persisted state from sessionStorage. Returns an empty object if
 * nothing is stored or if the stored payload is malformed.
 */
export function readSubmissionsListStorage(): Partial<SubmissionsListPersistedState> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(SUBMISSIONS_LIST_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};

    const result: Partial<SubmissionsListPersistedState> = {};
    for (const key of SUBMISSIONS_LIST_PERSISTED_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === "string") {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * Write the current state to sessionStorage. Silently no-ops on failure
 * (e.g. storage disabled, quota exceeded).
 */
export function writeSubmissionsListStorage(state: SubmissionsListPersistedState): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SUBMISSIONS_LIST_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}
